/**
 * Field-by-field comparison of a suggestion's payload with the target's current state, for the
 * moderator's review. Creates have no current state (every key is "new"). Values are rendered as
 * plain lines of text so the diff reads the same in any language.
 */
import type { SuggestionDetail } from "@divespots/contracts";

export type DiffKind = "changed" | "added";

export interface DiffRow {
  key: string;
  before: string[];
  after: string[];
  kind: DiffKind;
}

const LOCALES = ["de", "en", "fr", "it"];

function lines(value: unknown): string[] {
  if (value === null || value === undefined || value === "") return [];
  if (Array.isArray(value)) {
    return value.flatMap((v) => {
      if (v && typeof v === "object" && "from" in v && "to" in v) {
        const p = v as { from: string; to: string; reason?: Record<string, string> };
        // every language of the reason, not just the first one
        const reason = p.reason
          ? LOCALES.filter((l) => p.reason?.[l]).map((l) => `${l}: ${p.reason?.[l]}`)
          : [];
        return [`${p.from} – ${p.to}${reason.length ? ` (${reason.join(" / ")})` : ""}`];
      }
      return lines(v);
    });
  }
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    if ("lat" in o && "lon" in o) return [`${o.lat}, ${o.lon}`];
    const keys = Object.keys(o);
    // i18n texts: one line per language, in a fixed order
    if (keys.every((k) => LOCALES.includes(k))) {
      return LOCALES.filter((l) => o[l] !== undefined).map((l) => `${l}: ${String(o[l])}`);
    }
    return keys.map((k) => `${k}: ${String(o[k])}`);
  }
  return [String(value)];
}

const same = (a: string[], b: string[]) => a.length === b.length && a.every((x, i) => x === b[i]);

/** The current value of `key` on the target, or undefined when there is no target. */
function currentOf(current: SuggestionDetail["current"], key: string): unknown {
  if (!current) return undefined;
  const target = current.type === "spot" ? current.spot : current.object;
  return (target as Record<string, unknown>)[key];
}

/** One row per payload key; rows whose value equals the current one are dropped. */
export function diffRows(
  payload: Record<string, unknown>,
  current: SuggestionDetail["current"],
): DiffRow[] {
  const rows: DiffRow[] = [];
  for (const [key, value] of Object.entries(payload)) {
    const after = lines(value);
    const before = lines(currentOf(current, key));
    if (current && same(before, after)) continue;
    rows.push({ key, before, after, kind: current ? "changed" : "added" });
  }
  return rows;
}
